<template>
  <div id="app">
    <v-app :dark="darkTheme">
      <tree-drawer></tree-drawer>
      <right-drawer></right-drawer>
      <main-toolbar></main-toolbar>
      <fab-help-dial v-if="loggedIn && flagEnableHelpPopUp" :buttons="articles"/>
      <v-content class="main-router-view">
        <v-alert type="error" icon="priority_high" class="my-0" dismissible v-model="showCriticalAlerts">
          {{$t('announcements.panel.criticalBanner')}}
        </v-alert>
        <router-view></router-view>
      </v-content>
      <v-footer color="primary">
        <v-container>
          <v-layout row justify-space-between align-center>
            <img id="footer_logo" src="/static/img/solutionpath_logo.svg"/>
            <div class="primary-text">
              &copy; {{footerYear}}
            </div>
            <div class="primary-text">
              {{$t('footer.build', {version: '4.2.0'})}}
            </div>
          </v-layout>
        </v-container>
      </v-footer>
      <v-snackbar
        :id="elementId"
        :timeout="informationTimeout"
        top
        right
        v-model="informationSnackbar"
      >
        {{$t(informationText)}}
      </v-snackbar>
      <v-snackbar
        :timeout="undoTimeout"
        bottom
        right
        v-model="undoSnackbar"
      >
        {{undoText}}
        <v-btn id="undo-button" flat color="accent" @click="undoAction">{{$t('undo')}}</v-btn>
      </v-snackbar>
      <v-snackbar
        :timeout="logoutTimeout"
        bottom
        right
        v-model="logoutSnackbar"
      >
        {{ $t('logged_out_snackbar') }}
      </v-snackbar>

      <v-snackbar
        :timeout="serverErrorTimeout"
        color="error"
        bottom
        right
        v-model="serverError"
      >
        {{serverErrorMsg}}
      </v-snackbar>

      <v-snackbar
        :id="toasterId"
        :timeout="informationTimeout"
        bottom
        v-model="InformationToaster"
      >
        {{$t(informationToasterText)}}
      </v-snackbar>

      <login-modal />


    </v-app>
  </div>
</template>

<script>
  import Vue from 'vue'
  import * as actionTypes from './store/action-types'
  import { mapGetters, mapState } from 'vuex'
  import {
    LOGOUT_SNACKBAR_OFF,
    SET_DRAWER_STATE,
    SET_INFORMATION_SNACKBAR_STATE, SET_INFORMATION_TOASTER_STATE,
    SET_UNDO_STATE_SYNC
  } from './store/mutation-types'
  import RightDrawer from './components/RightDrawer'
  import TreeDrawer from './components/TreeDrawer'
  import { messageBus, messageBusTopics } from './MessageBus'
  import { undoTypes } from './store/modules/undo'
  import { getTextColor } from './HelperMethods'
  import Color from 'color'
  import MainToolbar from './components/navbar/MainToolbar'
  import { HAS_FEATURE_FLAG, GET_ANNOUNCEMENTS_SEVERITY } from './store/getter-types'
  import LoginModal from './components/login/LoginModal'
  import FabHelpDial from './components/misc/fab/FabHelpDial'
  import moment from 'moment-es6'
  import { refreshPageMixin } from './mixins/refresh-page.mixin'

  export default {
    components: {
      MainToolbar,
      RightDrawer,
      TreeDrawer,
      LoginModal,
      FabHelpDial
    },
    mixins: [refreshPageMixin],
    name: 'app',
    data () {
      return {
        number: 13,
        Vue: Vue,
        logoutTimeout: 2500,
        undoTimeout: 10000,
        informationTimeout: 2000,
        serverErrorTimeout: 2500,
        serverError: null,
        serverErrorMsg: '',
        isMenuOpen: false,
        showCriticalAlerts: false,
        footerYear: moment().format('YYYY')
      }
    },
    mounted () {
      messageBus.$on(messageBusTopics.SERVER_ERROR, errorText => {
        this.serverErrorMsg = errorText
        this.serverError = true
      })
      this.showCriticalAlerts = this.severity === 'CRITICAL'
      this.addCommentCssRules()
      this.initVersionCheck('version.json')
    },
    methods: {
      addCommentCssRules () {
        const color = Color(this.$vuetify.theme.primary).lighten(this.lighten4)
        const secondaryColor = Color(this.$vuetify.theme.secondary).lighten(this.lighten4)
        for (let i = 0; i < document.styleSheets.length; i++) {
          if (document.styleSheets[i].ownerNode.attributes.custom) {
            document.styleSheets[i].insertRule(`.comment-main{ background-color: ${color}}`, 0)
            document.styleSheets[i].insertRule(`.comment-main{ color: ${getTextColor(color)}}`, 0)
            document.styleSheets[i].insertRule(`.comment-main:after{ border-color: transparent ${color}}`, 0)
            document.styleSheets[i].insertRule(`.comment-secondary{ background-color: ${secondaryColor}}`, 0)
            document.styleSheets[i].insertRule(`.comment-secondary{ color: ${getTextColor(secondaryColor)}}`, 0)
            document.styleSheets[i].insertRule(`.comment-secondary:after{ border-color: transparent ${secondaryColor}}`, 0)
            document.styleSheets[i].insertRule(`.primary-text{ color: ${getTextColor(this.$vuetify.theme.primary)} !important}`, 0)
            document.styleSheets[i].insertRule(`.secondary-text{ color: ${getTextColor(this.$vuetify.theme.secondary)} !important}`, 0)
            document.styleSheets[i].insertRule(`.info-text{ color: ${getTextColor(this.$vuetify.theme.info)} !important}`, 0)
          }
        }
      },
      undoAction () {
        switch (this.$store.state.undo.type) {
          case undoTypes.INTERVENTION_CREATION:
            this.$store.commit(SET_DRAWER_STATE, true)
            this.undoSnackbar = false
            break
          case undoTypes.INTERVENTION_EDIT:
            this.$store.commit(SET_DRAWER_STATE, true)
            this.undoSnackbar = false
            break
          case undoTypes.EXEMPTION_CREATION:
            this.$store.commit(SET_DRAWER_STATE, true)
            this.undoSnackbar = false
            break
          case undoTypes.EXEMPTION_EDIT:
            this.$store.commit(SET_DRAWER_STATE, true)
            this.undoSnackbar = false
            break
          case undoTypes.INTERVENTION_DELETION:
            this.$store.dispatch(actionTypes.UNDO_DELETE_INTERVENTION_BY_ID, this.$store.state.intervention.lastDeletedId)
            this.undoSnackbar = false
            break
          case undoTypes.ADMIN_ALERT_EMAIL_DELETION:
            const payload = {
              emailId: this.$store.state.admin.lastDeletedAlertEmailId,
              toBeDeleted: false
            }
            this.$store.dispatch(actionTypes.DELETE_ADMIN_ALERT_EMAIL, payload)
            break
          case undoTypes.ANNOUNCEMENT_DELETION:
            this.$store.dispatch(actionTypes.DELETE_ANNOUNCEMENT, {id: this.$store.state.announcement.lastDeletedId, delete: false})
            this.undoSnackbar = false
            break
          case undoTypes.SAVE_FILTER_CREATION:
            this.$store.commit(SET_DRAWER_STATE, true)
            this.undoSnackbar = false
            break
          case undoTypes.SAVE_FILTER_DELETION:
            this.$store.dispatch(actionTypes.DELETE_SAVED_FILTER, { id: this.$store.state.students.lastDeletedFilterId, toBeDeleted: false })
            this.undoSnackbar = false
            break
          case undoTypes.CHART_DELETED:
            this.$store.dispatch(actionTypes.DELETE_DASHBOARD_CHART, { id: this.$store.state.dashboard.lastDeletedChartId, toBeDeleted: false })
            this.undoSnackbar = false
            break
          case undoTypes.DATA_SET_DELETED:
            this.$store.dispatch(actionTypes.DELETE_DATA_SET_ITEM, { id: this.$store.state.admin.lastDeletedDataSetId, toBeDeleted: false })
            this.undoSnackbar = false
            break
          case undoTypes.ALERT_DELETED:
            this.$store.dispatch(actionTypes.DELETE_ALERT, { alertId: this.$store.state.admin.lastDeletedAlertId, toBeDeleted: false })
            this.undoSnackbar = false
            break
          case undoTypes.EMAIL_TEMPLATE_DELETED:
            this.$store.dispatch(actionTypes.DELETE_EMAIL_TEMPLATE_ITEM, { id: this.$store.state.admin.lastDeletedEmailTemplateId, toBeDeleted: false })
            this.undoSnackbar = false
            break
          default:
            console.warn(`Found no handler for undo type: ${this.$store.state.undo.type}`)
        }
      }
    },
    watch: {
      announcements () {
        this.showCriticalAlerts = this.severity === 'CRITICAL'
      }
    },
    computed: {
      ...mapGetters({
        severity: GET_ANNOUNCEMENTS_SEVERITY,
        hasFeatureFlag: HAS_FEATURE_FLAG
      }),
      ...mapState({
        informationText: state => state.information.snackbarText,
        informationToasterText: state => state.information.toasterText,
        lighten4: state => state.config.lighten4,
        darkTheme: state => state.profile.darkTheme,
        announcements: state => state.announcement.announcements,
        loggedIn: state => state.profile.loggedIn,
        moduleHelp: state => state.profile.module.help,
        functionHelpPopUp: state => state.profile.functions.helpPopUp,
        optionEnableHelpPopUp: state => state.profile.options.enable.helpPopUp
      }),
      logoutSnackbar: {
        get () {
          return this.$store.state.profile.loggedOutSnackbar
        },
        set () {
          this.$store.commit(LOGOUT_SNACKBAR_OFF)
        }
      },
      undoSnackbar: {
        get () {
          return this.$store.state.undo.state
        },
        set (value) {
          this.$store.commit(SET_UNDO_STATE_SYNC, value)
        }
      },
      informationSnackbar: {
        get () {
          return this.$store.state.information.snackbar
        },
        set (value) {
          this.$store.commit(SET_INFORMATION_SNACKBAR_STATE, value)
        }
      },
      InformationToaster: {
        get () {
          return this.$store.state.information.toaster
        },
        set (value) {
          this.$store.commit(SET_INFORMATION_TOASTER_STATE, value)
        }
      },
      undoText () {
        return this.$t(this.$store.state.undo.type)
      },
      version () {
        return this.$store.state.config.version
      },
      flagEnableHelpPopUp () {
        return this.hasFeatureFlag(this.moduleHelp + '.' + this.functionHelpPopUp + '.' + this.optionEnableHelpPopUp)
      },
      articles () {
        return this.$t('student.help.article_ids')
      },
      elementId () {
        return `${this.$t(this.informationText).split(' ').join('-').toLowerCase()}-snackbar`
      },
      toasterId () {
        return `${this.$t(this.informationToasterText).split(' ').join('-').toLowerCase()}-toaster`
      }
    }
  }
</script>

<style scoped>
  .main-router-view {
    /*margin-top: 48px;*/
  }

  .select-header-menu {
    height: 4rem;
    padding-top: 15px;
  }

  .float-right {
    float: right;
  }

  #footer_logo {
    height: 28px;
  }

  #app .footer > .container {
    margin: 0 auto;
    padding-top: 0px;
    padding-bottom: 0px;
  }
</style>



// WEBPACK FOOTER //
// src/App.vue